import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import { useAuth, startGoogleLogin } from "@/lib/auth";
import { useLocale } from "@/lib/locale";
import { fetchLibrary, removeFromLibrary } from "@/lib/api";
import { Loader2, Trash2, Bookmark, Eye, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const TABS = [
  { id: "watchlist", icon: Bookmark, key: "watchlist" },
  { id: "watched", icon: Eye, key: "watched" },
  { id: "following", icon: Star, key: "following" },
];

export default function Profile() {
  const { user, loading: authLoading } = useAuth() || {};
  const { t } = useLocale();
  const navigate = useNavigate();
  const [tab, setTab] = useState("watchlist");
  const [library, setLibrary] = useState({ watchlist: [], watched: [], following: [] });
  const [loading, setLoading] = useState(true);

  const load = () => fetchLibrary().then(setLibrary).finally(() => setLoading(false));
  useEffect(() => {
    if (authLoading) return;
    if (!user) return;
    load();
  }, [authLoading, user]);

  if (authLoading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-amber" /></div>;
  }

  if (!user) {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="max-w-2xl mx-auto px-6 py-32 text-center">
          <h1 className="font-display text-4xl text-white">{t("profile")}</h1>
          <p className="mt-3 text-zinc-400 font-body">Sign in to manage your watchlist and library.</p>
          <Button
            onClick={startGoogleLogin}
            className="mt-8 bg-amber hover:bg-amber-hover text-obsidian font-heading"
            data-testid="profile-login-btn"
          >
            {t("sign_in")}
          </Button>
        </div>
      </div>
    );
  }

  const items = library[tab] || [];

  const remove = async (titleId) => {
    await removeFromLibrary(titleId);
    toast.success(t("remove"));
    load();
  };

  return (
    <div className="min-h-screen">
      <Header />
      <main className="max-w-6xl mx-auto px-6 py-14" data-testid="profile-main">
        <div className="flex items-center gap-4">
          {user.picture && <img src={user.picture} alt={user.name} className="h-16 w-16 rounded-full border border-white/10 object-cover" />}
          <div>
            <h1 className="font-display text-4xl text-white">{user.name}</h1>
            <p className="text-zinc-500 font-body text-sm">{user.email}</p>
          </div>
        </div>

        <div className="mt-8 flex gap-2 border-b border-white/10" data-testid="profile-tabs">
          {TABS.map((T) => (
            <button
              key={T.id}
              onClick={() => setTab(T.id)}
              className={`inline-flex items-center gap-1.5 px-4 py-2.5 font-heading text-sm transition border-b-2 -mb-px ${
                tab === T.id ? "text-amber border-amber" : "text-zinc-400 border-transparent hover:text-zinc-200"
              }`}
              data-testid={`profile-tab-${T.id}`}
            >
              <T.icon className="h-4 w-4" /> {t(T.key)} ({library[T.id]?.length || 0})
            </button>
          ))}
        </div>

        {loading ? (
          <div className="py-20 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-amber" /></div>
        ) : items.length === 0 ? (
          <div className="mt-10 border border-white/10 rounded-md p-10 text-center bg-obsidian-surface/60" data-testid={`profile-empty-${tab}`}>
            <p className="font-display text-2xl text-white">{t(`no_${tab}`)}</p>
            <Button onClick={() => navigate("/")} className="mt-6 bg-amber hover:bg-amber-hover text-obsidian font-heading">
              {t("browse_catalog")}
            </Button>
          </div>
        ) : (
          <ul className="mt-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-5 stagger" data-testid={`profile-list-${tab}`}>
            {items.map((item) => (
              <li key={item.id} className="relative group border border-white/10 rounded-md overflow-hidden bg-obsidian-surface hover:border-amber/40 transition" data-testid={`profile-item-${item.id}`}>
                <Link to={`/title/${item.id}`}>
                  <div className="aspect-[2/3] overflow-hidden">
                    <img src={item.poster} alt={item.title} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                  <div className="p-3">
                    <h3 className="font-display text-base text-white line-clamp-2">{item.title}</h3>
                    <p className="text-xs text-zinc-500 font-heading">{item.year}</p>
                  </div>
                </Link>
                <button
                  onClick={() => remove(item.id)}
                  className="absolute top-2 right-2 p-1.5 rounded-full bg-black/60 backdrop-blur text-zinc-400 hover:text-red-400 opacity-0 group-hover:opacity-100 transition"
                  data-testid={`profile-remove-${item.id}`}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </main>
    </div>
  );
}
